import { lazy, type ComponentType } from 'react';

/**
 * Stale-chunk recovery.
 *
 * Every deploy emits new hashed filenames and replaces the old ones. A browser
 * holding the PREVIOUS index.html asks for a chunk that no longer exists and the
 * lazy `import()` rejects — the customer sees "Failed to fetch dynamically
 * imported module". At our deploy pace this hits real sessions.
 *
 * The correct fix for the common cause (we shipped) is to fetch the new shell:
 * a full `location.reload()` pulls a fresh index.html and therefore the new
 * chunk names. But a reload on a GENUINELY broken deploy would trap the customer
 * in a loop that is far worse than the original error. So the automatic reload
 * is allowed exactly ONCE, guarded across the reload by sessionStorage; the
 * second failure surfaces the error UI (which carries a manual reload the
 * customer drives themselves).
 *
 * The reload is a FALLBACK, not the primary defence — raad-devops retains
 * previous assets so an open session survives a deploy without reloading at all.
 * Nothing here assumes the reload is the only path.
 */

const RELOAD_FLAG = 'raad.chunkReloadAttempted';

// Prevents a second reload within the SAME JS execution context (e.g. the
// `vite:preloadError` event and the React error boundary both firing for one
// incident). A real reload starts a fresh context, so this resets naturally.
let reloadInFlight = false;

/**
 * True when `err` looks like a failed dynamic import / preload — across the
 * message wording the major browsers use. Match on the message, never on a
 * single browser's exact string.
 *   Chrome:  "Failed to fetch dynamically imported module: <url>"
 *   Firefox: "error loading dynamically imported module: <url>"
 *   Safari:  "Importing a module script failed."
 *   Vite preload helper: "Unable to preload CSS for <url>"
 */
export function isChunkLoadError(err: unknown): boolean {
  let msg = '';
  if (err instanceof Error) msg = err.message;
  else if (typeof err === 'string') msg = err;
  else if (err && typeof err === 'object' && 'message' in err) {
    msg = String((err as { message: unknown }).message ?? '');
  }
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload|dynamically imported module/i.test(
    msg,
  );
}

// A reload is only guardable if we can remember, across the reload, that we
// already tried. Without storage we CANNOT guard the loop — so we refuse to
// auto-reload at all and let the error UI (with its manual button) take over.
// This is what makes the loop impossible in every environment, private mode
// included.
function canGuardReload(): boolean {
  try {
    const probe = '__raad_chunk_probe__';
    sessionStorage.setItem(probe, '1');
    sessionStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * Decide, and enact, what to do about a chunk-load failure.
 *
 * Returns `'reloading'` when an automatic reload has been (or already is being)
 * triggered — the caller should keep showing a neutral placeholder, the page is
 * about to navigate. Returns `'error'` when the one automatic reload has already
 * been spent (or cannot be guarded) — the caller should show the error UI.
 *
 * Idempotent within a context and across the reload: at most ONE automatic
 * reload per incident, then it stops.
 */
export function chunkErrorAction(): 'reloading' | 'error' {
  if (reloadInFlight) return 'reloading';
  if (!canGuardReload()) return 'error';
  if (sessionStorage.getItem(RELOAD_FLAG) === '1') return 'error';

  sessionStorage.setItem(RELOAD_FLAG, '1');
  reloadInFlight = true;
  try {
    window.location.reload();
  } catch {
    /* non-navigating environments (tests) — the flag is what matters */
  }
  return 'reloading';
}

/**
 * Clear the one-shot guard after a chunk has successfully loaded, so a LATER
 * stale-chunk incident (a second deploy while the tab stayed open) can reload
 * again. Called from the lazy wrapper on a successful import.
 */
export function markChunkLoadRecovered(): void {
  reloadInFlight = false;
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}

/**
 * The customer-driven reload from the error UI. Deliberate, so it is NOT
 * guarded — it must work every time it is pressed, even after the automatic
 * one-shot has already tripped. Never let the loop guard disable this: it is the
 * control on the screen we built to rescue the customer.
 */
export function manualReload(): void {
  window.location.reload();
}

/**
 * Vite fires `vite:preloadError` on the window when the preload of a
 * dynamically imported module's assets fails (production builds only). Handle it
 * as one of two independent mechanisms — the other is a rejected `import()`
 * reaching the route error boundary. We do not rely on either alone.
 */
export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Keep control of the outcome rather than letting it surface as an
    // unhandled rejection; then run the same guarded action as the boundary.
    event.preventDefault();
    chunkErrorAction();
  });
}

/**
 * `React.lazy` for route chunks, wrapped so a successful import clears the
 * one-shot reload guard. Logic identical to a bare `lazy(factory)` otherwise.
 */
export function lazyRoute<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().then((mod) => {
      markChunkLoadRecovered();
      return mod;
    }),
  );
}

/**
 * TEST SEAM — simulate the fresh JS execution context that a real
 * `location.reload()` would produce, WITHOUT clearing the sessionStorage flag
 * that survives a reload. Lets a test prove the second failure (post-reload)
 * shows the error UI and does not reload again. Not used in app code.
 */
export function __resetReloadContextForTest(): void {
  reloadInFlight = false;
}
